import React, { useEffect, useRef, useState } from 'react'
import IconButton from './IconButton'

async function writeToClipboard(text) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch (_) {
  }

  try {
    const textarea = document.createElement('textarea')
    textarea.value = text
    textarea.setAttribute('readonly', '')
    textarea.style.position = 'absolute'
    textarea.style.left = '-9999px'
    document.body.appendChild(textarea)
    textarea.select()
    document.execCommand('copy')
    document.body.removeChild(textarea)
    return true
  } catch (_) {
    return false
  }
}

function resolveTextToCopy({ textToCopy, targetId }) {
  if (textToCopy !== undefined && textToCopy !== null) return String(textToCopy)
  if (!targetId) return ''

  const el = document.getElementById(targetId)
  if (!el) return ''

  if (Object.prototype.hasOwnProperty.call(el, 'value')) return String(el.value ?? '')
  return String(el.textContent ?? '')
}

export default function CopyButtonBase(
  {
    // self-copy mode
    targetId,
    textToCopy,

    onClick,
    justCopied, // boolean | undefined

    copiedDurationMs = 2000,

    tooltipCopyText = 'Copy',
    tooltipCopiedText = 'Copied',
    tooltipDelayMs = 700,
    disableTooltip = false,

    // styling
    variant = 'row', // 'row' | 'subtle'
    subtleOpacity = 0.2,
    idleIconStyle,
    hoverIconStyle,
    buttonClassName,
    buttonStyle,
    wrapperStyle,
    iconSize,
    ariaLabel = 'Copy',

    preventDefault = false,
    stopPropagation = false,
    disabled = false
  }
) {
  const [internalJustCopied, setInternalJustCopied] = useState(false)
  const timeoutRef = useRef(null)

  const effectiveJustCopied = justCopied !== undefined ? justCopied : internalJustCopied
  const iconName = effectiveJustCopied ? 'check' : 'copy outline'
  const effectiveAriaLabel = effectiveJustCopied ? tooltipCopiedText : ariaLabel
  const tooltipText = disableTooltip ? undefined : (effectiveJustCopied ? tooltipCopiedText : tooltipCopyText)
  const effectiveTooltipDelayMs = disableTooltip ? 0 : (effectiveJustCopied ? 0 : tooltipDelayMs)

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [])

  const doInternalCopy = async () => {
    const text = resolveTextToCopy({ textToCopy, targetId })
    if (!text) return false
    return await writeToClipboard(text)
  }

  const handleClick = async (e) => {
    if (preventDefault) e.preventDefault()
    if (stopPropagation) e.stopPropagation()
    if (disabled) return

    if (onClick) {
      onClick(e)
      return
    }

    const ok = await doInternalCopy()
    if (!ok) return

    setInternalJustCopied(true)

    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    timeoutRef.current = setTimeout(() => setInternalJustCopied(false), copiedDurationMs)
  }

  // Only override icon styles when:
  // - the caller passes explicit styles, or
  // - variant === 'subtle' and we want to tweak opacity.
  const iconStyleProps = {
    ...(idleIconStyle !== undefined
      ? { idleIconStyle }
      : (variant === 'subtle' ? { idleIconStyle: { opacity: subtleOpacity } } : {})),
    ...(hoverIconStyle !== undefined
      ? { hoverIconStyle }
      : (variant === 'subtle' ? { hoverIconStyle: { opacity: 1 } } : {})),
  }

  return (
    <IconButton
      iconName={iconName}
      ariaLabel={effectiveAriaLabel}
      tooltip={tooltipText}
      tooltipDelayMs={effectiveTooltipDelayMs}
      tooltipVisible={disableTooltip ? false : ((hovered) => hovered || effectiveJustCopied)}
      iconSize={iconSize}
      buttonClassName={`btn text_or_icon_link btn-sm p-0${buttonClassName ? ` ${buttonClassName}` : ''}`}
      buttonStyle={buttonStyle}
      wrapperStyle={wrapperStyle}
      {...iconStyleProps}
      preventDefault={preventDefault}
      stopPropagation={stopPropagation}
      disabled={disabled}
      title={disableTooltip ? tooltipCopyText : undefined}
      onClick={handleClick}
    />
  )
}
